import type { ScoringMode } from '../../rules/hkos';
import { renderBrandHeader } from '../branding';
import { esc } from '../helpers';
import { updateMarker } from '../updateSafety';

export interface ModeSelectActions {
  choose(mode: ScoringMode): void;
  viewPastGames(): void;
  /** The Hand Calculator: score a hand without playing — nothing is recorded. */
  viewHandCalculator(): void;
  /** The venue's leaderboard: rules set by the venue, every player signs in with a nickname and PIN. */
  openLeaderboard(): void;
  /** Just looks at the board (the running season, or the last finished one) — no game, no sign-in. */
  viewLeaderboard(): void;
}

const MODES: { mode: ScoringMode; label: string; description: string }[] = [
  {
    mode: 'fun',
    label: 'Fun Mode',
    description: 'Only the round winner gains points. Nobody else ever loses any — draws pay nobody.',
  },
  {
    mode: 'serious',
    label: 'Serious Mode',
    description: 'Traditional scoring. Winners gain, losers lose — the bigger the hand, the bigger the swing.',
  },
];

export function renderModeSelect(
  root: HTMLElement,
  defaultMode: ScoringMode,
  actions: ModeSelectActions,
  /** Whether an admin has switched Leaderboard mode on. When off the option is not drawn at all. */
  showLeaderboard: boolean,
): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    ${updateMarker('idle')}
    <div class="card">
      <h1>Mahjong Scorer</h1>
      <p class="muted">Choose how this game will be scored. You'll pick again next game.</p>

      <div class="stack">
        ${MODES.map(
          (m) => `
          <button type="button" class="mode-option ${m.mode === defaultMode ? '' : 'secondary'}" data-mode="${m.mode}" style="width:100%;text-align:left">
            <div style="font-weight:600">${m.label}</div>
            <div class="muted" style="margin-top:0.2rem">${m.description}</div>
          </button>
        `,
        ).join('')}
        ${
          showLeaderboard
            ? `<button type="button" class="mode-option secondary" id="leaderboardBtn" style="width:100%;text-align:left">
          <div style="font-weight:600">Leaderboard</div>
          <div class="muted" style="margin-top:0.2rem">Play for the venue's leaderboard. The venue sets the rules, and every player signs in with a nickname and PIN.</div>
        </button>`
            : ''
        }
      </div>

      ${
        showLeaderboard
          ? '<button type="button" id="viewBoardBtn" class="secondary" style="width:100%;margin-top:0.75rem">View the leaderboard</button>'
          : ''
      }
      <button type="button" id="pastGamesBtn" class="ghost" style="width:100%;margin-top:0.75rem">Past Games</button>
      <button type="button" id="handCalcBtn" class="ghost" style="width:100%;margin-top:0.5rem">🧮 Hand Calculator</button>

      <p class="app-signature">Built by ICH</p>
      <p class="app-signature-sub" title="When this copy of the app was built — reload the page to fetch whatever was most recently deployed.">
        Updated ${esc(new Date(__BUILD_TIME__).toLocaleString())}
      </p>
    </div>
  `;

  root.querySelectorAll<HTMLButtonElement>('.mode-option[data-mode]').forEach((btn) => {
    btn.addEventListener('click', () => {
      actions.choose(btn.dataset['mode'] as ScoringMode);
    });
  });

  root.querySelector<HTMLButtonElement>('#leaderboardBtn')?.addEventListener('click', () => {
    actions.openLeaderboard();
  });

  root.querySelector<HTMLButtonElement>('#viewBoardBtn')?.addEventListener('click', () => {
    actions.viewLeaderboard();
  });

  root.querySelector<HTMLButtonElement>('#pastGamesBtn')!.addEventListener('click', () => {
    actions.viewPastGames();
  });

  root.querySelector<HTMLButtonElement>('#handCalcBtn')!.addEventListener('click', () => {
    actions.viewHandCalculator();
  });
}
